// src/calculators/severance.js — калкулатор „Обезщетения при напускане":
// чл. 224 (неползван отпуск), чл. 220 (неспазено предизвестие) и чл. 222
// (оставане без работа / болест / пенсиониране) от КТ.
//
// Тук е САМО екранът. Цялата нормативна сметка живее в services/severance.js
// и е обща за тестовете и панела — така числата на екрана са точно тези,
// които тестовете доказват. Нищо не се записва в базата.

import { el, clear, field, input, row } from '../shared/dom.js';
import { dateInput } from '../shared/dates.js';
// Правното основание (задача 6/6) — цитатите са сверени в lex.bg.
import { withLaw, hintify } from './law.js';
import {
  vacationDays, calc224, calc220, calc222_1, calc222_2, calc222_3,
  EUR_BGN_RATE,
} from '../../services/severance.js';

// Запетая като цялата система („540,00 €"); знакът € идва от повикващите.
const f2 = (x) => (Math.round((Number(x) || 0) * 100) / 100).toFixed(2).replace('.', ',');
const okDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const MONTHS = ['януари', 'февруари', 'март', 'април', 'май', 'юни',
  'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];

function monthSelect(value) {
  // Първата опция е ПРАЗНА — иначе браузърът мълчаливо избира „януари" и
  // коефициентът по чл. 18, ал. 2 се смята с грешен месец, без човек да
  // е избирал каквото и да е.
  return el('select', {},
    el('option', { value: '', text: '— избери —' }),
    ...MONTHS.map((name, i) =>
      el('option', { value: i + 1, text: name, selected: i + 1 === Number(value) })));
}
function numInput(attrs) {
  return input(Object.assign({ type: 'number', step: '0.01', min: '0' }, attrs));
}

export function buildSeveranceCalc() {
  // ---- Общи полета ---------------------------------------------------------
  const hireI = dateInput('');
  const termI = dateInput('');
  const baseI = numInput({ placeholder: '0.00' });          // чл. 228, ал. 1
  const baseMonthSel = monthSelect('');
  const baseYearI = numInput({ step: '1', placeholder: 'гггг' });
  // Месецът-база се предпопълва от датата на прекратяването (предходния
  // месец), но САМО докато потребителят не го е пипнал сам.
  let baseMonthDirty = false;

  // ---- Чл. 224: години с отпуск -------------------------------------------
  // Редовете по години се преизграждат при смяна на датите; въведеното се
  // пази тук по година, за да не се губи при преизграждане.
  const yearVals = new Map(); // year → { annual, used, deferred, excluded }
  const yearsBox = el('div', {});
  let yearsKey = '';

  const roundingSel = el('select', {},
    el('option', { value: 'math', text: 'към най-близкия цял ден (по подразбиране)' }),
    el('option', { value: 'up', text: 'винаги нагоре (в полза на работника)' }),
    el('option', { value: 'none', text: 'без закръгляване (2 знака)' }));
  const rateModeSel = el('select', {},
    el('option', { value: 'base-month', text: 'Базов месец с ≥10 отработени дни (чл. 177, ал. 1)' }),
    el('option', { value: 'contract', text: 'Уговорена заплата — няма такъв месец (чл. 177, ал. 2)' }));
  const bmMonthSel = monthSelect('');
  const bmYearI = numInput({ step: '1', placeholder: 'гггг' });
  const bmWorkedI = numInput({ step: '1', placeholder: 'дни' });
  const bmGrossI = numInput({ placeholder: '0.00' });
  const bmCurrencyHint = el('div', { class: 'calc-note', style: 'margin-top:-10px' });
  const contractI = numInput({ placeholder: '0.00' });
  const coefCb = input({ type: 'checkbox' });

  const bmBox = el('div', {},
    row(field('Базов месец', bmMonthSel, { size: 's' }), field('Базова година', bmYearI, { size: 's' })),
    row(field('Отработени дни в базовия месец', bmWorkedI, { size: 's' }),
      field('Начислено БТВ за базовия месец', bmGrossI, { size: 's' })),
    // Класът прослужено време е добавка с ПОСТОЯНЕН характер (НСОРЗ чл. 15,
    // ал. 1) — напомняне, защото често го изпускат от базата.
    el('p', { style: 'font-size:12px;color:var(--muted);margin:2px 0 8px', text: 'В БТВ влизат основната заплата и добавките с постоянен характер — включително класът прослужено време (НСОРЗ чл. 15, ал. 1).' }),
    bmCurrencyHint);
  const contractBox = el('div', {},
    field('Уговорена месечна заплата с постоянните добавки, €', contractI, { size: 's' }));

  const out224 = el('div', { class: 'calc-result' });

  // ---- Чл. 220 -------------------------------------------------------------
  const noticeModeSel = el('select', {},
    el('option', { value: 'months', text: 'цели месеци' }),
    el('option', { value: 'workDays', text: 'работни дни' }));
  const noticeCountI = numInput({ step: '1', placeholder: '0' });

  // ---- Чл. 222 -------------------------------------------------------------
  const a1MonthsI = numInput({ step: '1', placeholder: '0' });
  const a1DaysI = numInput({ step: '1', placeholder: '0' });
  const a1AgreedI = numInput({ step: '1', value: 1 });
  const born1960Cb = input({ type: 'checkbox' });
  const a1NewSalaryI = numInput({ placeholder: '0.00' });
  const a1NewDaysI = numInput({ step: '1', placeholder: '0' });
  const a2OnCb = input({ type: 'checkbox' });
  const a2FiveYearsCb = input({ type: 'checkbox' });
  const a2NotReceivedCb = input({ type: 'checkbox' });
  const a3OnCb = input({ type: 'checkbox' });
  const a3TenYearsCb = input({ type: 'checkbox' });

  // ---- Резултати -----------------------------------------------------------
  const resultsBox = el('div', {});
  const notesBox = el('div', {});

  function yearRow(year) {
    const v = yearVals.get(year) || { annual: 20, used: 0, deferred: '', excluded: 0 };
    yearVals.set(year, v);
    const annualI = numInput({ step: '1', value: v.annual, placeholder: '20' });
    const usedI = numInput({ step: '1', value: v.used });
    const deferredI = numInput({ step: '1', value: v.deferred, placeholder: 'гггг' });
    const excludedI = numInput({ step: '1', value: v.excluded });
    annualI.addEventListener('input', () => { v.annual = annualI.value; recompute(); });
    usedI.addEventListener('input', () => { v.used = usedI.value; recompute(); });
    deferredI.addEventListener('input', () => { v.deferred = deferredI.value; recompute(); });
    excludedI.addEventListener('input', () => { v.excluded = excludedI.value; recompute(); });
    return el('div', {},
      el('h4', { style: 'margin:10px 0 4px', text: 'Година ' + year }),
      row(
        field('Полагаеми дни • ' + year, annualI, { size: 's' }),
        field('Ползвани дни • ' + year, usedI, { size: 's' }),
        field('Отложен (чл. 176): причината отпадна през • ' + year, deferredI, { size: 's' }),
        field('Незачетени за стаж дни • ' + year, excludedI, { size: 's' })));
  }

  // Преизгражда редовете по години само когато обхватът от години реално
  // се смени — иначе всяко натискане на клавиш би триело фокуса.
  function syncYearRows(hire, term) {
    if (!okDate(hire) || !okDate(term) || hire > term) {
      if (yearsKey !== '') { yearsKey = ''; clear(yearsBox); }
      return;
    }
    const from = Number(hire.slice(0, 4));
    const to = Number(term.slice(0, 4));
    const key = from + '-' + to;
    if (key === yearsKey) return;
    yearsKey = key;
    clear(yearsBox);
    for (let y = from; y <= to; y++) yearsBox.appendChild(yearRow(y));
  }

  function yearsInput(hire, term) {
    const from = Number(hire.slice(0, 4));
    const to = Number(term.slice(0, 4));
    const years = [];
    for (let y = from; y <= to; y++) {
      const v = yearVals.get(y) || { annual: 20, used: 0, deferred: '', excluded: 0 };
      years.push({
        year: y,
        // Празно поле → услугата слага законовия минимум 20; изрична нула
        // се предава като нула (не бива тихо да става 20).
        annualDays: v.annual === '' || v.annual == null ? null : Number(v.annual),
        usedDays: Number(v.used) || 0,
        deferredCauseEndedYear: Number(v.deferred) || undefined,
        excludedServiceDays: Number(v.excluded) || 0,
      });
    }
    return years;
  }

  function line(label, value, big) {
    return el('div', { class: 'calc-line' + (big ? ' big' : '') },
      el('span', { text: label }), el('span', { text: value }));
  }
  function warnLine(text) { return el('div', { class: 'sev-warn', text }); }

  function recompute() {
    const hire = hireI.dataset.iso || '';
    const term = termI.dataset.iso || '';
    syncYearRows(hire, term);

    // Предпопълване на месеца-база: месецът ПРЕДИ прекратяването (чл. 228, ал. 1).
    if (!baseMonthDirty && okDate(term)) {
      const y = Number(term.slice(0, 4));
      const m = Number(term.slice(5, 7));
      bmSet(baseMonthSel, m === 1 ? 12 : m - 1);
      baseYearI.value = m === 1 ? y - 1 : y;
    }

    // Левовете важат за базов месец до 2025 г. — показваме го до полето,
    // за да не се чуди човекът в каква валута да въведе заплатата.
    const bmY = Number(bmYearI.value) || 0;
    bmCurrencyHint.textContent = (bmY && bmY <= 2025)
      ? 'Месец от ' + bmY + ' г.: въведете сумата в ЛЕВА — превалутира се по фиксирания курс 1 € = ' + EUR_BGN_RATE + ' лв.'
      : '';

    const rows = [];   // редове за таблицата: { label, gross, ins, tax, net }
    const noteGroups = []; // { label, notes: [...] }
    // Предупрежденията (давност, МРЗ-под, грешки) се показват И при сборната
    // таблица — секцията може да е сгъната и иначе се губят.
    const globalWarns = [];
    let employerIns = 0;

    // ---- чл. 224 ----
    clear(out224);
    if (okDate(hire) && okDate(term) && hire <= term) {
      try {
        const common = {
          hireDate: hire, terminationDate: term,
          years: yearsInput(hire, term), rounding: roundingSel.value,
        };
        const mode = rateModeSel.value;
        const bmMonthVal = Number(bmSelVal(bmMonthSel));
        const bmReady = mode === 'contract'
          ? Number(contractI.value) > 0
          : (bmY > 0 && bmMonthVal > 0 && Number(bmWorkedI.value) > 0 && Number(bmGrossI.value) > 0);
        // МРЗ-подът изисква ред в таблицата за датата на прекратяването —
        // преди 2025 г. няма такъв и парите не могат да се сметнат коректно.
        const minWageOk = term >= '2025-01-01';

        const opts = Object.assign(common, {
          mode,
          baseMonth: { year: bmY, month: bmMonthVal },
          grossForBaseMonth: Number(bmGrossI.value) || 0,
          grossCurrency: (bmY && bmY <= 2025) ? 'BGN' : 'EUR',
          workedDays: Number(bmWorkedI.value) || 0,
          contractMonthly: Number(contractI.value) || 0,
          applyCoefficient: coefCb.checked,
        });

        const pushWarn = (w) => { out224.appendChild(warnLine(w)); globalWarns.push(w); };
        if (bmReady && minWageOk) {
          const r = calc224(opts);
          r.perYear.forEach((y) => out224.appendChild(line(
            'Отпуск ' + y.year + ' (' + y.months + ' пълни месеца)',
            y.expired ? 'погасен по давност' : y.remaining + ' дни')));
          out224.appendChild(line('Дни за обезщетяване', String(r.days), true));
          out224.appendChild(line('Среднодневна ставка', f2(r.rate) + ' €/ден'));
          if (r.rateWithCoef !== null && r.rateWithCoef !== r.rateNoCoef) {
            // Показваме и двете СУМИ, не само ставките — човекът сравнява пари.
            out224.appendChild(line('Ставка: без / с коеф. чл. 18, ал. 2',
              f2(r.rateNoCoef) + ' € / ' + f2(r.rateWithCoef) + ' €'));
            out224.appendChild(line('Бруто: без / с коефициента',
              f2(r.rateNoCoef * r.days) + ' € / ' + f2(r.rateWithCoef * r.days) + ' €'));
          }
          r.warnings.forEach(pushWarn);
          if (r.gross > 0) {
            rows.push({ label: 'Неползван отпуск — чл. 224', gross: r.gross, ins: 0, tax: r.tax, net: r.net });
            noteGroups.push({ label: 'Чл. 224', notes: r.notes });
          }
        } else {
          // Дните могат да се покажат и преди да е попълнена ставката.
          const v = vacationDays(common);
          v.perYear.forEach((y) => out224.appendChild(line(
            'Отпуск ' + y.year + ' (' + y.months + ' пълни месеца)',
            y.expired ? 'погасен по давност' : y.remaining + ' дни')));
          out224.appendChild(line('Дни за обезщетяване', String(v.totalDays), true));
          v.warnings.forEach(pushWarn);
          if (bmReady && !minWageOk) {
            pushWarn('Калкулаторът поддържа прекратявания от 01.01.2025 г. нататък — за по-стара дата допълнете таблицата с МРЗ в services/severance.js.');
          } else {
            out224.appendChild(el('div', { class: 'calc-note', text: 'Попълнете ставката (базов месец или уговорена заплата), за да се сметнат парите.' }));
          }
        }
      } catch (e) {
        const msg = 'Чл. 224 не може да се сметне: ' + e.message;
        out224.appendChild(warnLine(msg));
        globalWarns.push(msg);
      }
    } else {
      out224.appendChild(el('div', { class: 'calc-note', text: 'Въведете дата на постъпване и дата на прекратяване (последен ден).' }));
    }

    // ---- чл. 220 и чл. 222 (обща месечна база) ----
    const mb = Number(baseI.value) || 0;
    const baseMonth = { year: Number(baseYearI.value) || 0, month: Number(bmSelVal(baseMonthSel)) };
    const baseMonthOk = baseMonth.year > 0 && baseMonth.month > 0;
    const termYear = okDate(term) ? Number(term.slice(0, 4)) : undefined;

    // Всяко обезщетение се смята в собствен try — грешка в едно поле не
    // бива тихо да скрива редовете на останалите (случваше се точно това).
    const mbMonth = baseMonthOk ? baseMonth : null;

    try {
      const n = Number(noticeCountI.value) || 0;
      if (mb > 0 && n > 0) {
        if (noticeModeSel.value === 'months' || baseMonthOk) {
          const unserved = noticeModeSel.value === 'months' ? { months: n } : { workDays: n };
          const r = calc220({ monthlyBase: mb, baseMonth: mbMonth, noticeUnserved: unserved });
          rows.push({ label: 'Неспазено предизвестие — чл. 220', gross: r.gross, ins: 0, tax: r.tax, net: r.net });
          noteGroups.push({ label: 'Чл. 220', notes: r.notes });
        } else {
          noteGroups.push({ label: 'Чл. 220', notes: ['За неспазено предизвестие в РАБОТНИ ДНИ попълнете „Месец на базата" и „Година на базата" — среднодневната ставка се смята от тях.'] });
        }
      }
    } catch (e) {
      noteGroups.push({ label: 'Чл. 220', notes: ['Проверете входа: ' + e.message] });
    }

    try {
      const a1m = Number(a1MonthsI.value) || 0;
      const a1d = Number(a1DaysI.value) || 0;
      if (mb > 0 && (a1m > 0 || a1d > 0)) {
        if (a1m === 0 && a1d > 0 && !baseMonthOk) {
          noteGroups.push({ label: 'Чл. 222, ал. 1', notes: ['За непълен месец без работа попълнете „Месец на базата" и „Година на базата".'] });
        } else {
          const r = calc222_1({
            monthlyBase: mb, baseMonth: mbMonth, months: a1m, workDays: a1d,
            agreedMonths: Number(a1AgreedI.value) || 1,
            bornBefore1960: born1960Cb.checked,
            year: termYear,
            lowerPaid: (Number(a1NewSalaryI.value) > 0 && Number(a1NewDaysI.value) > 0)
              ? { newMonthly: Number(a1NewSalaryI.value), workDays: Number(a1NewDaysI.value) }
              : null,
          });
          employerIns = r.employerInsurance;
          rows.push({ label: 'Оставане без работа — чл. 222, ал. 1', gross: r.gross, ins: r.personalInsurance, tax: r.tax, net: r.net });
          noteGroups.push({ label: 'Чл. 222, ал. 1', notes: r.notes });
          if (a1d > 0 && !baseMonthOk) {
            noteGroups.push({ label: 'Чл. 222, ал. 1', notes: ['Работните дни не са включени — попълнете „Месец на базата" и „Година на базата".'] });
          }
        }
      }
    } catch (e) {
      noteGroups.push({ label: 'Чл. 222, ал. 1', notes: ['Проверете входа: ' + e.message] });
    }

    try {
      if (mb > 0 && a2OnCb.checked) {
        const r = calc222_2({ monthlyBase: mb, eligible5y: a2FiveYearsCb.checked, notReceivedLast5y: a2NotReceivedCb.checked });
        rows.push({ label: 'Болест — чл. 222, ал. 2', gross: r.gross, ins: 0, tax: r.tax, net: r.net });
        noteGroups.push({ label: 'Чл. 222, ал. 2', notes: r.notes });
      }

      if (mb > 0 && a3OnCb.checked) {
        const r = calc222_3({ monthlyBase: mb, tenYearsAtEmployer: a3TenYearsCb.checked });
        rows.push({ label: 'Пенсиониране — чл. 222, ал. 3', gross: r.gross, ins: 0, tax: r.tax, net: r.net });
        noteGroups.push({ label: 'Чл. 222, ал. 3', notes: r.notes });
      }
    } catch (e) {
      noteGroups.push({ label: 'Чл. 222', notes: ['Проверете входа: ' + e.message] });
    }

    // ---- Таблицата с общия сбор ----
    clear(resultsBox);
    clear(notesBox);
    // Първо предупрежденията — те трябва да се виждат дори секцията-източник
    // да е сгъната, и дори когато няма нито един сметнат ред.
    globalWarns.forEach((w) => resultsBox.appendChild(warnLine(w)));
    if (rows.length) {
      const sum = rows.reduce((t, r) => ({
        gross: t.gross + r.gross, ins: t.ins + r.ins, tax: t.tax + r.tax, net: t.net + r.net,
      }), { gross: 0, ins: 0, tax: 0, net: 0 });
      resultsBox.appendChild(el('table', { class: 'sev-results' },
        el('thead', {}, el('tr', {},
          el('th', { text: 'Обезщетение' }), el('th', { class: 'num', text: 'Бруто €' }),
          el('th', { class: 'num', text: 'Осигуровки €' }), el('th', { class: 'num', text: 'Данък €' }),
          el('th', { class: 'num', text: 'Нето €' }))),
        el('tbody', {}, ...rows.map((r) => el('tr', {},
          el('td', { text: r.label }), el('td', { class: 'num', text: f2(r.gross) }),
          el('td', { class: 'num', text: f2(r.ins) }), el('td', { class: 'num', text: f2(r.tax) }),
          el('td', { class: 'num', text: f2(r.net) })))),
        el('tfoot', {}, el('tr', { class: 'sev-total' },
          el('td', { text: 'Общо' }), el('td', { class: 'num', text: f2(sum.gross) }),
          el('td', { class: 'num', text: f2(sum.ins) }), el('td', { class: 'num', text: f2(sum.tax) }),
          el('td', { class: 'num', text: f2(sum.net) })))));
      if (employerIns > 0) {
        resultsBox.appendChild(el('div', { class: 'calc-note',
          text: 'Осигуровки за сметка на работодателя по чл. 222, ал. 1: ' + f2(employerIns) + ' € — отделен разход, не намалява нетото.' }));
      }
    } else {
      resultsBox.appendChild(el('div', { class: 'calc-note', text: 'Попълнете поне едно обезщетение, за да се появи сборната таблица.' }));
    }
    // Бележките се показват ВИНАГИ — включително подсказките „какво липсва",
    // когато няма нито един ред (иначе празната таблица не обяснява нищо).
    noteGroups.forEach((g) => notesBox.appendChild(el('div', { class: 'calc-note' },
      el('strong', { text: g.label + ': ' }),
      g.notes.join(' '))));

    // Видимост на полетата според избрания ред за ставката.
    bmBox.style.display = rateModeSel.value === 'base-month' ? '' : 'none';
    contractBox.style.display = rateModeSel.value === 'contract' ? '' : 'none';
  }

  // select-ът за месец се чете/пише на две места — малки помощници, за да
  // не се бърка value ('' при непопълнено) с номер на месец.
  function bmSelVal(sel) { return sel.value || 0; }
  function bmSet(sel, m) { sel.value = String(m); }

  [hireI, termI, baseI, roundingSel, rateModeSel, bmMonthSel, bmYearI, bmWorkedI, bmGrossI,
    contractI, coefCb, noticeModeSel, noticeCountI, a1MonthsI, a1DaysI, a1AgreedI, born1960Cb,
    a1NewSalaryI, a1NewDaysI, a2OnCb, a2FiveYearsCb, a2NotReceivedCb, a3OnCb, a3TenYearsCb]
    .forEach((n) => { n.addEventListener('input', recompute); n.addEventListener('change', recompute); });
  baseMonthSel.addEventListener('change', () => { baseMonthDirty = true; recompute(); });
  baseYearI.addEventListener('input', () => { baseMonthDirty = true; recompute(); });

  const panel = el('div', { class: 'panel calc-panel calc-severance' },
    el('h3', { class: 'calc-title', text: 'Обезщетения при прекратяване (чл. 224, 220, 222 КТ)' }),
    el('div', { class: 'calc-fields' },
      row(field('Постъпване на работа', hireI, { size: 's' }),
        field('Прекратяване — последен работен ден', termI, { size: 's' })),
      row(hintify(field('Месечна база (чл. 228, ал. 1), €', baseI, { size: 's' }),
        'чл. 228, ал. 1 КТ', 'Базата е полученото БТВ за месеца, предхождащ месеца на основанието, или последното получено месечно БТВ.'),
        field('Месец на базата', baseMonthSel, { size: 's' }),
        field('Година на базата', baseYearI, { size: 's' })),
      el('div', { class: 'calc-note', text: 'Месечната база е полученото брутно възнаграждение за месеца, предхождащ прекратяването — ползва се за чл. 220 и чл. 222.' })),

    el('details', { open: true },
      el('summary', { text: 'Неползван отпуск (чл. 224)' }),
      yearsBox,
      row(field('Закръгляване на дробните дни', roundingSel),
        field('Среднодневна ставка по', rateModeSel)),
      bmBox,
      contractBox,
      field('Приложи коефициента по чл. 18, ал. 2 НСОРЗ (спорно за чл. 224 — по подразбиране не)', coefCb),
      out224),

    el('details', {},
      el('summary', { text: 'Неспазено предизвестие (чл. 220)' }),
      row(field('Неспазено предизвестие — мярка', noticeModeSel, { size: 'm' }),
        field('Неспазено предизвестие — брой', noticeCountI, { size: 's' })),
      el('div', { class: 'calc-note', text: 'Срокът на предизвестието е 30 дни до 3 месеца за безсрочен договор (чл. 326, ал. 2). Дължи се от страната, която не го спазва — в двете посоки.' })),

    el('details', {},
      el('summary', { text: 'Оставане без работа / болест / пенсия (чл. 222)' }),
      row(field('Месеци без работа (ал. 1)', a1MonthsI, { size: 's' }),
        field('Работни дни без работа — непълен месец (ал. 1)', a1DaysI, { size: 's' }),
        field('Уговорен срок в месеци (ал. 1)', a1AgreedI, { size: 's' })),
      row(field('Нова месечна заплата при по-нископлатена работа, € (ал. 1)', a1NewSalaryI, { size: 's' }),
        field('Дни на по-нископлатената работа (ал. 1)', a1NewDaysI, { size: 's' })),
      field('Роден преди 01.01.1960 г. (без УПФ, само фонд „Пенсии")', born1960Cb),
      el('hr'),
      field('Прекратяване поради болест (ал. 2)', a2OnCb),
      row(field('Има поне 5 г. общ трудов стаж (ал. 2)', a2FiveYearsCb, { size: 's' }),
        field('Няма такова обезщетение през последните 5 г. (ал. 2)', a2NotReceivedCb, { size: 's' })),
      el('hr'),
      field('Пенсиониране (ал. 3)', a3OnCb),
      field('10 г. стаж при същия работодател или групата — последните 20 г. (ал. 3)', a3TenYearsCb)),

    resultsBox,
    notesBox);

  recompute();
  return withLaw(panel, {
    when: [
      'Служител напуска с неползван платен отпуск — обезщетение по чл. 224 към последния ден.',
      'Страна прекратява преди да изтече предизвестието — дължи БТВ за неспазения срок (чл. 220).',
      'Съкращение/закриване и лицето остава без работа — до 1 месец БТВ по чл. 222, ал. 1.',
      'Пенсиониране: 2 месеца БТВ, а при 10 години при същия работодател (или групата) през последните 20 — 6 месеца (чл. 222, ал. 3).',
      'Сборната таблица отива в досието на лицето с „Запази в досие".',
    ],
    quotes: [
      { ref: 'чл. 224, ал. 1 КТ (изм. ДВ, бр. 58/2010; РКС № 12/2010)', text: 'При прекратяване на трудовото правоотношение работникът или служителят има право на парично обезщетение за неизползвания платен годишен отпуск за текущата календарна година пропорционално на времето, което се признава за трудов стаж, и за неизползвания отпуск, отложен по реда на чл. 176, правото за който не е погасено по давност.' },
      { ref: 'чл. 220, ал. 1 КТ', text: 'Страната, която има право да прекрати трудовото правоотношение с предизвестие, може да го прекрати и преди да изтече срокът на предизвестието, при което дължи на другата страна обезщетение в размер на брутното трудово възнаграждение на работника или служителя за неспазения срок на предизвестието.' },
      { ref: 'чл. 222, ал. 3 КТ (изм. ДВ, бр. 107/2020)', text: 'При прекратяване на трудовото правоотношение, след като работникът или служителят е придобил право на пенсия за осигурителен стаж и възраст, независимо от основанието за прекратяването, той има право на обезщетение от работодателя в размер на брутното му трудово възнаграждение за срок от 2 месеца, а ако е придобил при същия работодател или в същата група предприятия 10 години трудов стаж през последните 20 години – на обезщетение в размер на брутното му трудово възнаграждение за срок от 6 месеца.' },
      { ref: 'чл. 228, ал. 1 КТ', text: 'Брутното трудово възнаграждение за определяне на обезщетенията по този раздел е полученото от работника или служителя брутно трудово възнаграждение за месеца, предхождащ месеца, в който е възникнало основанието за съответното обезщетение, или последното получено от работника или служителя месечно брутно трудово възнаграждение, доколкото друго не е предвидено.' },
    ],
  });
}
